import { useEffect, useState } from "react";
import { useFieldArray, useForm, useWatch, FormProvider } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import {
  useRecordStockInOrderDelivery,
  getGetStockInOrderQueryKey,
  getListStockInOrdersQueryKey,
  getListStockInQueryKey,
  useListInventoryItems,
  useListHsnCodes,
  useListChargeTypes,
  type InventoryItem,
  type InventoryHsnCode,
  type StockInOrder,
  type StockInOrderCartLine,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2, Plus, Trash2, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { InvoiceUploader } from "./stock-in-form";
import { StockInLineItemsTable, type LineItemRow } from "./stock-in-line-items-table";

// Phase 22-06 — Record a delivery (shipment) against a stock-in order.
// Pre-populated from the order's remaining (ordered − delivered) cart lines.
// Operator edits actual qty + price, enters invoice details, and toggles GST claimed.
// Off-cart lines (substitutions) can be added.

interface DeliveryChargeRow {
  label: string;
  amount: number | string;
  gstRate: number;
}

interface DeliveryFormValues {
  transactionDate: string;
  invoiceNumber: string;
  invoiceDate: string;
  invoicePdfUrl: string;
  gstInputClaimed: boolean;
  priceInclusiveGst: boolean;
  notes: string;
  lineItems: LineItemRow[];
  charges: DeliveryChargeRow[];
}

interface StockInOrderDeliveryFormProps {
  order: StockInOrder;
  onSaved: () => void;
  onDiscard: () => void;
}

/**
 * Compute delivered qty for a cart-line item across all deliveries.
 * We iterate over delivery receipts' lineItems.
 */
function deliveredQtyForItem(order: StockInOrder, itemId: number): number {
  const deliveries = order.deliveries ?? [];
  let total = 0;
  for (const d of deliveries) {
    for (const li of d.lineItems ?? []) {
      if (li.itemId === itemId) {
        total += Number(li.quantityReceived ?? 0);
      }
    }
  }
  return total;
}

function buildDefaultLineItems(cartLines: StockInOrderCartLine[], _order: StockInOrder): LineItemRow[] {
  // Form is reached via the "Non Stock-In Delivered" PO picker — surface
  // ONLY the lines in that state (status='Delivered' AND qty still missing
  // from receipts). Cart lines that already have a receipt covering them
  // (or aren't flagged Delivered yet) stay off this form to keep the
  // operator focused on closing the status-vs-receipt gap.
  //
  // Uses cl.quantityDelivered (server-computed join) — the older client-
  // side `deliveredQtyForItem(order, cl.itemId)` walked d.lineItems which
  // the order detail GET doesn't return, so it always read as 0 and
  // every cart line ended up included.
  const result: LineItemRow[] = [];
  for (const cl of cartLines) {
    if (cl.status !== "Delivered") continue;
    const delivered = Number((cl as { quantityDelivered?: number | string | null }).quantityDelivered ?? 0);
    const remaining = Math.max(0, cl.quantityOrdered - delivered);
    if (remaining <= 0) continue;
    result.push({
      itemId: cl.itemId,
      quantityReceived: remaining,
      // Pre-fill Qty Ordered with the cart line's full ordered qty so the
      // operator sees the original PO commitment alongside what they're
      // actually receiving. Variance against this drives later reconciliation.
      quantityOrdered: cl.quantityOrdered,
      unitPriceActual: Number(cl.expectedUnitPrice),
      gstRate: 0,
      gstAmount: 0,
      lineTotal: remaining * Number(cl.expectedUnitPrice),
    });
  }
  return result;
}

export function StockInOrderDeliveryForm({ order, onSaved, onDiscard }: StockInOrderDeliveryFormProps) {
  const { toast } = useToast();
  const qc = useQueryClient();

  const defaultLineItems = buildDefaultLineItems(order.cartLines ?? [], order);

  const form = useForm<DeliveryFormValues>({
    defaultValues: {
      transactionDate: new Date().toISOString().slice(0, 10),
      invoiceNumber: "",
      invoiceDate: "",
      invoicePdfUrl: "",
      gstInputClaimed: false,
      // Default the toggle to ON because buildDefaultLineItems populates
      // unitPriceActual with the PO cart's expected_unit_price, which is
      // stored INCL of GST (that's what vendors quote on invoices). If the
      // toggle were OFF at populate time, the incl price would land as-is
      // into a field that submit treats as base — the receipt would be
      // over-billed. STK-IN-098 was hit by the mirror bug (populated incl,
      // toggle OFF at save) and stored ~₹305 short across 15 lines.
      priceInclusiveGst: true,
      notes: "",
      lineItems: defaultLineItems.length > 0
        ? defaultLineItems
        : [{ itemId: null, quantityReceived: 1, unitPriceActual: "", gstRate: 0, gstAmount: 0 }],
      charges: [],
    },
  });

  const chargesArray = useFieldArray({ control: form.control, name: "charges" });
  const { data: chargeTypes = [] } = useListChargeTypes({ status: "Active" });

  // PO-populated lines come from buildDefaultLineItems() with gstRate hardcoded
  // to 0 (the cart line doesn't carry an item's GST rate). Resolve each line's
  // rate once items + HSN master have loaded, using the same logic the picker's
  // itemInfo() uses in stock-in-line-items-table.tsx: item.gstRateOnSale wins
  // when > 0, else fall through to the HSN master's mapped rate. Runs once when
  // both queries settle.
  const { data: allItems = [] } = useListInventoryItems({ status: "Active" });
  const { data: hsnCodes = [] } = useListHsnCodes({ status: "Active" });
  const [gstAutofillDone, setGstAutofillDone] = useState(false);
  useEffect(() => {
    if (gstAutofillDone) return;
    if (allItems.length === 0) return;
    const lines = form.getValues("lineItems") ?? [];
    if (!Array.isArray(lines) || lines.length === 0) return;
    let wrote = 0;
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i] as LineItemRow;
      const currentRate = Number(l.gstRate ?? 0);
      if (currentRate > 0) continue;
      if (l.itemId == null) continue;
      const it = allItems.find((x: InventoryItem) => x.id === l.itemId);
      if (!it) continue;
      const own = it.gstRateOnSale != null ? Number(it.gstRateOnSale) : null;
      const hsn = it.hsnSacCode ?? null;
      const mapped = hsn ? hsnCodes.find((c: InventoryHsnCode) => c.code === hsn) : undefined;
      const rate = own != null && own > 0
        ? own
        : mapped != null
          ? Number(mapped.gstRate)
          : null;
      if (rate != null && rate > 0) {
        form.setValue(`lineItems.${i}.gstRate`, rate, { shouldDirty: true });
        wrote++;
      }
    }
    if (wrote > 0 || allItems.length > 0) setGstAutofillDone(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allItems, hsnCodes]);

  // Per-row 'include on this invoice' toggle. Default everything checked.
  // Tracked by row index — useFieldArray keeps indices contiguous. Unchecked
  // rows stay visible in the form (with a faded look + 'skipped' badge) but
  // get filtered out on submit; the operator can re-tick to put them back.
  const [excludedIndices, setExcludedIndices] = useState<Set<number>>(new Set());
  const onToggleIncluded = (index: number, included: boolean) => {
    setExcludedIndices((prev) => {
      const next = new Set(prev);
      if (included) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const deliveryMut = useRecordStockInOrderDelivery({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getGetStockInOrderQueryKey(order.id) });
        qc.invalidateQueries({ queryKey: getListStockInOrdersQueryKey() });
        qc.invalidateQueries({ queryKey: getListStockInQueryKey() });
        toast({ title: "Delivery recorded." });
        onSaved();
      },
      onError: (err: unknown) => {
        // Surface over-delivery warning if the server sends one
        const msg =
          err != null &&
          typeof err === "object" &&
          "message" in err &&
          typeof (err as { message?: unknown }).message === "string"
            ? (err as { message: string }).message
            : "Failed to record delivery. Please try again.";
        toast({ title: msg, variant: "destructive" });
      },
    },
  });

  function onSubmit(values: DeliveryFormValues) {
    // Drop unticked rows (operator skipped them — they stay on the PO for a
    // future receipt) before the itemId-null filter.
    const lines = values.lineItems
      .filter((_, idx) => !excludedIndices.has(idx))
      .filter((l) => l.itemId != null);
    if (lines.length === 0) {
      toast({ title: "Add at least one delivery line.", variant: "destructive" });
      return;
    }

    // Confirm with the operator when the stock-in amount is LESS than the PO
    // remaining — server allows it (stock-in <= PO), but the operator should
    // know the gap will roll into a future receipt against the same PO.
    const stockInTotal = lines.reduce(
      (s, l) => s + Number(l.quantityReceived) * Number(l.unitPriceActual) + Number(l.gstAmount ?? 0),
      0,
    );
    const remainingValue = poRemainingValue(order);
    if (remainingValue != null && stockInTotal + 1 < remainingValue) {
      const diff = remainingValue - stockInTotal;
      const ok = window.confirm(
        `This stock-in is ₹${diff.toFixed(2)} less than the PO remaining of ₹${remainingValue.toFixed(2)}. ` +
        `The difference will need a future receipt against the same PO. Continue?`,
      );
      if (!ok) return;
    }

    deliveryMut.mutate({
      id: order.id,
      data: {
        transactionDate: values.transactionDate,
        invoiceNumber: values.invoiceNumber || undefined,
        invoiceDate: values.invoiceDate || undefined,
        invoicePdfUrl: values.invoicePdfUrl || undefined,
        gstInputClaimed: values.gstInputClaimed,
        notes: values.notes || undefined,
        lineItems: lines.map((l) => {
          // Mirror stock-in-form.tsx submit: derive the pre-GST base from
          // the entered unit price + the priceInclusiveGst toggle, and
          // (re)compute gstAmount authoritatively at submit — the form's
          // stored gstAmount was previously never updated after the initial
          // 0 default, so this form was silently sending 0 GST.
          const qty = Number(l.quantityReceived) || 0;
          const price = Number(l.unitPriceActual) || 0;
          const rate = Number(l.gstRate) || 0;
          const inclusive = Boolean(values.priceInclusiveGst);
          const unitBase = inclusive ? price / (1 + rate / 100) : price;
          const baseUnitPrice = Math.round(unitBase * 100) / 100;
          const gstAmount = Math.round(qty * baseUnitPrice * rate) / 100;
          return {
          itemId: l.itemId!,
          quantityReceived: qty,
          unitPriceActual: baseUnitPrice,
          gstAmount,
          batchNumber: l.batchNumber ?? undefined,
          // Drop empty strings to undefined before send. RHF gives back ""
          // for date/text inputs the operator never touched, but zod
          // (`coerce.date().nullish()`) treats "" as an invalid Date and
          // 400s the whole request — even though the line itself is fine.
          // Affects any line whose item has the Expires flag.
          expiryDate: l.expiryDate ? l.expiryDate : undefined,
          storageLocation: l.storageLocation ? l.storageLocation : undefined,
          };
        }),
        charges: (values.charges ?? [])
          .filter((c) => (c.label ?? "").trim() && Number(c.amount) > 0)
          .map((c) => ({
            label: String(c.label).trim(),
            amount: Number(c.amount),
            gstRate: c.gstRate != null ? Number(c.gstRate) : null,
          })),
      },
    });
  }

  /**
   * Compute the PO's remaining pre-tax value across cart lines:
   *   SUM((qty_ordered - already_delivered) * expected_unit_price)
   * Returns null if cart lines aren't loaded.
   *
   * Uses cl.quantityDelivered (server-computed join across all receipts).
   * The old walk over o.deliveries[].lineItems always read 0 because the
   * order detail GET doesn't include per-receipt line items, so the
   * confirm dialog reported the FULL PO as remaining and warned the
   * operator about a phantom shortfall on every receipt.
   */
  function poRemainingValue(o: StockInOrder): number | null {
    const lines = o.cartLines ?? [];
    if (lines.length === 0) return null;
    let total = 0;
    for (const cl of lines) {
      const delivered = Number((cl as { quantityDelivered?: number | string | null }).quantityDelivered ?? 0);
      const remaining = Math.max(0, cl.quantityOrdered - delivered);
      total += remaining * Number(cl.expectedUnitPrice);
    }
    return total;
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
        <div>
          <p className="text-base font-semibold">Record Delivery</p>
          <p className="text-sm text-muted-foreground mt-0.5">
            Pre-filled with remaining undelivered quantities and expected prices. Edit actual values below.
          </p>
        </div>

        {/* Invoice details */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Transaction date</label>
            <Input type="date" {...form.register("transactionDate")} />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Invoice date</label>
            <Input type="date" {...form.register("invoiceDate")} />
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium">Invoice number</label>
          <Input placeholder="INV-2024-001…" {...form.register("invoiceNumber")} />
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium">Upload Invoice (PDF / Image)</label>
          <InvoiceUploader
            value={form.watch("invoicePdfUrl") || null}
            onChange={(url) => form.setValue("invoicePdfUrl", url ?? "")}
          />
        </div>

        <div className="flex items-center gap-3">
          <Switch
            id="gstInputClaimed"
            checked={form.watch("gstInputClaimed")}
            onCheckedChange={(v) => form.setValue("gstInputClaimed", v)}
          />
          <label htmlFor="gstInputClaimed" className="text-sm font-medium cursor-pointer">
            GST input credit claimed on this invoice
          </label>
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium">Notes</label>
          <Textarea placeholder="Delivery notes…" rows={2} {...form.register("notes")} />
        </div>

        <Separator />

        {/* Delivery line items — reuse StockInLineItemsTable */}
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-sm font-semibold">Line Items</p>
              <p className="text-xs text-muted-foreground">
                Actual quantities + prices for this shipment. Add off-cart lines for substitutions.
              </p>
            </div>
            {/* Same toggle as the plain Stock-In receipt form — inclusive
                splits the embedded GST out of the entered price; exclusive
                adds it on top. Submit uses the toggle to compute the base
                stored on the receipt. */}
            <div className="flex items-center gap-2 shrink-0">
              <span className={`text-xs ${form.watch("priceInclusiveGst") ? "text-muted-foreground" : "font-medium"}`}>Price excl. GST</span>
              <Switch
                checked={Boolean(form.watch("priceInclusiveGst"))}
                onCheckedChange={(c) => form.setValue("priceInclusiveGst", c, { shouldDirty: true })}
                aria-label="Toggle whether unit prices include GST"
              />
              <span className={`text-xs ${form.watch("priceInclusiveGst") ? "font-medium" : "text-muted-foreground"}`}>Price incl. GST</span>
            </div>
          </div>
          <StockInLineItemsTable
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            control={form.control as any}
            showIncludeCheckbox
            excludedIndices={excludedIndices}
            onToggleIncluded={onToggleIncluded}
            priceInclusiveGst={Boolean(form.watch("priceInclusiveGst"))}
          />
        </div>

        {/* Additional Charges — Delivery / Handling / Platform Fee / Surge.
            Same schema and persistence path as the plain Stock-In receipt
            form; server folds them into the receipt's grand_total. */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold">Additional Charges</p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => chargesArray.append({ label: "", amount: 0, gstRate: 18 })}
            >
              <Plus className="h-4 w-4 mr-1" /> Add charge
            </Button>
          </div>
          {chargesArray.fields.length === 0 ? (
            <p className="text-xs text-muted-foreground rounded-md border border-dashed px-3 py-2">
              No extra charges. Add delivery / handling / platform / surge fees here — manage the list in Inventory → Charges.
            </p>
          ) : (
            <div className="space-y-2">
              {chargesArray.fields.map((field, i) => {
                const amount = Number(form.watch(`charges.${i}.amount`)) || 0;
                const rate = Number(form.watch(`charges.${i}.gstRate`)) || 0;
                const gst = Math.round(amount * rate) / 100;
                return (
                  <div key={field.id} className="grid grid-cols-[1fr_110px_90px_auto] gap-2 items-end rounded-md border bg-card p-2">
                    <div>
                      <label className="text-[11px] text-muted-foreground mb-1 block">Charge</label>
                      <Select
                        value={(form.watch(`charges.${i}.label`) as string) || ""}
                        onValueChange={(v) => {
                          form.setValue(`charges.${i}.label`, v, { shouldDirty: true });
                          const t = chargeTypes.find((ct) => ct.name === v);
                          if (t?.defaultGstRate != null) {
                            form.setValue(`charges.${i}.gstRate`, Number(t.defaultGstRate), { shouldDirty: true });
                          }
                        }}
                      >
                        <SelectTrigger><SelectValue placeholder="Select charge…" /></SelectTrigger>
                        <SelectContent>
                          {chargeTypes.map((ct) => (
                            <SelectItem key={ct.id} value={ct.name}>{ct.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-[11px] text-muted-foreground mb-1 block">Amount ₹</label>
                      <Input
                        type="number"
                        step="0.01"
                        className="font-mono tabular-nums"
                        value={(form.watch(`charges.${i}.amount`) as number | string) ?? ""}
                        onChange={(e) => form.setValue(
                          `charges.${i}.amount`,
                          e.target.value === "" ? 0 : Number(e.target.value),
                          { shouldDirty: true },
                        )}
                      />
                    </div>
                    <div>
                      <label className="text-[11px] text-muted-foreground mb-1 block">GST %</label>
                      <select
                        className="flex h-10 w-full rounded-md border border-input bg-background px-2 py-2 text-sm font-mono tabular-nums ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                        value={String(rate)}
                        onChange={(e) => form.setValue(`charges.${i}.gstRate`, Number(e.target.value), { shouldDirty: true })}
                      >
                        {[0, 5, 12, 18, 28, 40].map((r) => <option key={r} value={r}>{r}%</option>)}
                      </select>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive h-9 w-9 p-0"
                      onClick={() => chargesArray.remove(i)}
                      aria-label="Remove charge"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    <p className="col-span-4 text-[11px] text-muted-foreground font-mono tabular-nums -mt-1">
                      GST ₹{gst.toFixed(2)} · line total ₹{(amount + gst).toFixed(2)}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <DeliveryTotalsSummary
          control={form.control}
          excludedIndices={excludedIndices}
          priceInclusiveGst={Boolean(form.watch("priceInclusiveGst"))}
        />

        <div className="flex gap-3 pt-2">
          <Button
            type="submit"
            disabled={deliveryMut.isPending}
            className="flex-1"
          >
            {deliveryMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Record Delivery
          </Button>
          <Button type="button" variant="outline" onClick={onDiscard}>
            Cancel
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}

/**
 * Renders Subtotal (excl. GST) + Total GST + Grand Total right above the
 * Record Delivery button. Watches lineItems live so the numbers update as
 * the operator edits qty / price / GST% / included-checkbox.
 *
 * Mirrors the plain Stock-In form's computed grandTotal formula:
 *   grandTotal = Σ(qty × base) + Σ(qty × base × rate/100)
 * where `base` respects the Price incl./excl. GST toggle.
 */
function DeliveryTotalsSummary({
  control,
  excludedIndices,
  priceInclusiveGst,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: any;
  excludedIndices: Set<number>;
  priceInclusiveGst: boolean;
}) {
  const lineItems = (useWatch({ control, name: "lineItems" }) as LineItemRow[] | undefined) ?? [];
  const charges = (useWatch({ control, name: "charges" }) as DeliveryChargeRow[] | undefined) ?? [];
  let itemsBase = 0;
  let itemsGst = 0;
  for (let i = 0; i < lineItems.length; i++) {
    if (excludedIndices.has(i)) continue;
    const li = lineItems[i]!;
    const qty = Number(li.quantityReceived) || 0;
    const price = Number(li.unitPriceActual) || 0;
    const rate = Number(li.gstRate) || 0;
    // Match submit-path rounding: round unit_base to 2 decimals per line
    // before qty × base and qty × base × rate so display totals equal what
    // gets persisted (avoids a few-paisa drift on multi-line receipts).
    const unitBaseRaw = priceInclusiveGst ? price / (1 + rate / 100) : price;
    const baseUnitPrice = Math.round(unitBaseRaw * 100) / 100;
    itemsBase += qty * baseUnitPrice;
    itemsGst += Math.round(qty * baseUnitPrice * rate) / 100;
  }
  let chargesBase = 0;
  let chargesGst = 0;
  for (const c of charges) {
    if (!(c.label ?? "").trim()) continue;
    const amount = Number(c.amount) || 0;
    if (amount <= 0) continue;
    const rate = Number(c.gstRate) || 0;
    chargesBase += amount;
    chargesGst += amount * (rate / 100);
  }
  const subtotal = Math.round(itemsBase * 100) / 100;
  const totalGst = Math.round((itemsGst + chargesGst) * 100) / 100;
  const chargesTotal = Math.round(chargesBase * 100) / 100;
  const grandTotal = Math.round((itemsBase + itemsGst + chargesBase + chargesGst) * 100) / 100;
  const fmt = (n: number) => `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div className="rounded-md border bg-muted/30 px-4 py-3 space-y-1.5 text-sm">
      <div className="flex items-center justify-between text-muted-foreground">
        <span>Subtotal (items, excl. GST)</span>
        <span className="font-mono tabular-nums">{fmt(subtotal)}</span>
      </div>
      {chargesTotal > 0 && (
        <div className="flex items-center justify-between text-muted-foreground">
          <span>Additional charges (excl. GST)</span>
          <span className="font-mono tabular-nums">{fmt(chargesTotal)}</span>
        </div>
      )}
      <div className="flex items-center justify-between text-muted-foreground">
        <span>Total GST</span>
        <span className="font-mono tabular-nums">{fmt(totalGst)}</span>
      </div>
      <div className="flex items-center justify-between font-semibold border-t pt-1.5 mt-1">
        <span>Grand Total</span>
        <span className="font-mono tabular-nums">{fmt(grandTotal)}</span>
      </div>
    </div>
  );
}
